import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";

// Small inline spinner — no icon library in this app (see Layout.jsx's
// plain "↗" and HRHome.jsx's emoji icons for the existing convention).
function Spinner({ size = 16 }) {
  return (
    <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

const emptyPhone = { phone_type: "Mobile", phone_number: "" };
const emptyEducation = { school_name: "", degree: "", field_of_study: "", graduation_year: "" };
const emptyEmployment = {
  employer_name: "", job_title: "", start_date: "", end_date: "",
  reason_for_leaving: "", supervisor_name: "", supervisor_phone: "",
};
const emptyReference = { reference_name: "", relationship: "", phone: "", email: "" };

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-6">
      <h2 className="text-lg font-semibold mb-1" style={{ color: "#154E93" }}>{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mb-5">{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block mb-4">
      <span className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-orange-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent";
const inputStyle = { "--tw-ring-color": "#154E93" };

function RepeatableRow({ onRemove, children }) {
  return (
    <div className="relative border border-gray-200 rounded-xl p-4 mb-3 bg-gray-50">
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
        aria-label="Remove"
      >
        <span className="text-base leading-none">✕</span>
      </button>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-8">{children}</div>
    </div>
  );
}

function AddButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-dashed hover:bg-orange-50 transition-colors"
      style={{ color: "#F3850C", borderColor: "#F3850C" }}
    >
      <span className="text-base leading-none">+</span> {label}
    </button>
  );
}

export default function PublicApply() {
  const [searchParams] = useSearchParams();
  const lockedJob = searchParams.get("job");

  const [jobTypes, setJobTypes] = useState({ field_duty: [], office: [] });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    job_type: lockedJob || "",
    first_name: "", last_name: "", other_names: "", date_of_birth: "", gender: "",
    email: "", address: "", city: "", state: "NH", zip: "",
    travel_radius_miles: "",
    work_authorized: "", age_18_plus: "", has_evv_smartphone: "", willing_background_check: "",
    applicant_notes: "",
  });
  const [phones, setPhones] = useState([{ ...emptyPhone }]);
  const [education, setEducation] = useState([{ ...emptyEducation }]);
  const [employmentHistory, setEmploymentHistory] = useState([{ ...emptyEmployment }]);
  const [references, setReferences] = useState([{ ...emptyReference }]);

  useEffect(() => {
    api.recruitment.jobTypes().then(setJobTypes).catch(() => {});
  }, []);

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  function updateRow(rows, setRows, idx, key, value) {
    setRows(rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.job_type) {
      setError("Please select the position you're applying for.");
      return;
    }
    if (!form.first_name || !form.last_name) {
      setError("Please enter your first and last name.");
      return;
    }

    setSubmitting(true);
    try {
      await api.recruitment.apply({
        ...form,
        phones: phones.filter((p) => p.phone_number.trim()),
        education: education.filter((ed) => ed.school_name.trim()),
        employment_history: employmentHistory.filter((eh) => eh.employer_name.trim()),
        references: references.filter((r) => r.reference_name.trim()),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#F5F8FC" }}>
        <div className="bg-white rounded-2xl shadow-sm p-10 max-w-md w-full text-center">
          <div className="text-5xl mb-4" style={{ color: "#154E93" }}>✓</div>
          <h1 className="text-xl font-bold mb-2" style={{ color: "#154E93" }}>Application received</h1>
          <p className="text-sm text-gray-600">
            Thank you for applying to JyBek Home Care Services. Our team will review your application
            and reach out if there's a good fit. We appreciate your interest in joining us.
          </p>
        </div>
      </div>
    );
  }

  const allJobTypes = [...jobTypes.field_duty, ...jobTypes.office];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F8FC" }}>
      <div className="text-white" style={{ backgroundColor: "#154E93" }}>
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🤝</span>
            <span className="text-sm uppercase tracking-wide text-white/70">JyBek Home Care Services</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Join our care team</h1>
          <p className="text-white/80 max-w-xl">
            We're always looking for compassionate, reliable people to support our clients and their
            families across New Hampshire. Tell us a bit about yourself below — it only takes a few minutes.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-8 md:py-10">
        <Section title="Position" subtitle="Which role are you applying for?">
          <Field label="Job Type" required>
            {lockedJob ? (
              <div className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
                {lockedJob}
              </div>
            ) : (
              <select
                className={inputClass}
                style={inputStyle}
                value={form.job_type}
                onChange={(e) => setField("job_type", e.target.value)}
                required
              >
                <option value="">Select a position…</option>
                {jobTypes.field_duty.length > 0 && (
                  <optgroup label="Field Duty">
                    {jobTypes.field_duty.map((j) => <option key={j} value={j}>{j}</option>)}
                  </optgroup>
                )}
                {jobTypes.office.length > 0 && (
                  <optgroup label="Office / Administrative">
                    {jobTypes.office.map((j) => <option key={j} value={j}>{j}</option>)}
                  </optgroup>
                )}
                {allJobTypes.length === 0 && <option value={form.job_type}>{form.job_type || "Loading…"}</option>}
              </select>
            )}
          </Field>
        </Section>

        <Section title="General Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <Field label="First Name" required>
              <input className={inputClass} style={inputStyle} value={form.first_name}
                onChange={(e) => setField("first_name", e.target.value)} required />
            </Field>
            <Field label="Last Name" required>
              <input className={inputClass} style={inputStyle} value={form.last_name}
                onChange={(e) => setField("last_name", e.target.value)} required />
            </Field>
            <Field label="Other Names">
              <input className={inputClass} style={inputStyle} value={form.other_names}
                onChange={(e) => setField("other_names", e.target.value)} />
            </Field>
            <Field label="Date of Birth">
              <input type="date" className={inputClass} style={inputStyle} value={form.date_of_birth}
                onChange={(e) => setField("date_of_birth", e.target.value)} />
            </Field>
            <Field label="Gender">
              <select className={inputClass} style={inputStyle} value={form.gender}
                onChange={(e) => setField("gender", e.target.value)}>
                <option value="">Prefer not to say</option>
                <option>Female</option>
                <option>Male</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Email">
              <input type="email" className={inputClass} style={inputStyle} value={form.email}
                onChange={(e) => setField("email", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Phone Numbers" subtitle="Add at least one number where we can reach you.">
          {phones.map((p, i) => (
            <RepeatableRow key={i} onRemove={() => setPhones(phones.filter((_, idx) => idx !== i))}>
              <Field label="Type">
                <select className={inputClass} style={inputStyle} value={p.phone_type}
                  onChange={(e) => updateRow(phones, setPhones, i, "phone_type", e.target.value)}>
                  <option>Mobile</option>
                  <option>Home</option>
                  <option>Work</option>
                </select>
              </Field>
              <Field label="Number">
                <input className={inputClass} style={inputStyle} value={p.phone_number}
                  onChange={(e) => updateRow(phones, setPhones, i, "phone_number", e.target.value)} />
              </Field>
            </RepeatableRow>
          ))}
          <AddButton label="Add phone number" onClick={() => setPhones([...phones, { ...emptyPhone }])} />
        </Section>

        <Section title="Address">
          <Field label="Street Address">
            <input className={inputClass} style={inputStyle} value={form.address}
              onChange={(e) => setField("address", e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
            <Field label="City">
              <input className={inputClass} style={inputStyle} value={form.city}
                onChange={(e) => setField("city", e.target.value)} />
            </Field>
            <Field label="State">
              <input className={inputClass} style={inputStyle} value={form.state}
                onChange={(e) => setField("state", e.target.value)} />
            </Field>
            <Field label="ZIP">
              <input className={inputClass} style={inputStyle} value={form.zip}
                onChange={(e) => setField("zip", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Education">
          {education.map((ed, i) => (
            <RepeatableRow key={i} onRemove={() => setEducation(education.filter((_, idx) => idx !== i))}>
              <Field label="School">
                <input className={inputClass} style={inputStyle} value={ed.school_name}
                  onChange={(e) => updateRow(education, setEducation, i, "school_name", e.target.value)} />
              </Field>
              <Field label="Degree / Certificate">
                <input className={inputClass} style={inputStyle} value={ed.degree}
                  onChange={(e) => updateRow(education, setEducation, i, "degree", e.target.value)} />
              </Field>
              <Field label="Field of Study">
                <input className={inputClass} style={inputStyle} value={ed.field_of_study}
                  onChange={(e) => updateRow(education, setEducation, i, "field_of_study", e.target.value)} />
              </Field>
              <Field label="Graduation Year">
                <input className={inputClass} style={inputStyle} value={ed.graduation_year}
                  onChange={(e) => updateRow(education, setEducation, i, "graduation_year", e.target.value)} />
              </Field>
            </RepeatableRow>
          ))}
          <AddButton label="Add school" onClick={() => setEducation([...education, { ...emptyEducation }])} />
        </Section>

        <Section title="Employment History">
          {employmentHistory.map((eh, i) => (
            <RepeatableRow key={i} onRemove={() => setEmploymentHistory(employmentHistory.filter((_, idx) => idx !== i))}>
              <Field label="Employer">
                <input className={inputClass} style={inputStyle} value={eh.employer_name}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "employer_name", e.target.value)} />
              </Field>
              <Field label="Job Title">
                <input className={inputClass} style={inputStyle} value={eh.job_title}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "job_title", e.target.value)} />
              </Field>
              <Field label="Start Date">
                <input type="date" className={inputClass} style={inputStyle} value={eh.start_date}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "start_date", e.target.value)} />
              </Field>
              <Field label="End Date">
                <input type="date" className={inputClass} style={inputStyle} value={eh.end_date}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "end_date", e.target.value)} />
              </Field>
              <Field label="Reason for Leaving">
                <input className={inputClass} style={inputStyle} value={eh.reason_for_leaving}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "reason_for_leaving", e.target.value)} />
              </Field>
              <Field label="Supervisor Name">
                <input className={inputClass} style={inputStyle} value={eh.supervisor_name}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "supervisor_name", e.target.value)} />
              </Field>
              <Field label="Supervisor Phone">
                <input className={inputClass} style={inputStyle} value={eh.supervisor_phone}
                  onChange={(e) => updateRow(employmentHistory, setEmploymentHistory, i, "supervisor_phone", e.target.value)} />
              </Field>
            </RepeatableRow>
          ))}
          <AddButton label="Add employer" onClick={() => setEmploymentHistory([...employmentHistory, { ...emptyEmployment }])} />
        </Section>

        <Section title="References">
          {references.map((r, i) => (
            <RepeatableRow key={i} onRemove={() => setReferences(references.filter((_, idx) => idx !== i))}>
              <Field label="Name">
                <input className={inputClass} style={inputStyle} value={r.reference_name}
                  onChange={(e) => updateRow(references, setReferences, i, "reference_name", e.target.value)} />
              </Field>
              <Field label="Relationship">
                <input className={inputClass} style={inputStyle} value={r.relationship}
                  onChange={(e) => updateRow(references, setReferences, i, "relationship", e.target.value)} />
              </Field>
              <Field label="Phone">
                <input className={inputClass} style={inputStyle} value={r.phone}
                  onChange={(e) => updateRow(references, setReferences, i, "phone", e.target.value)} />
              </Field>
              <Field label="Email">
                <input className={inputClass} style={inputStyle} value={r.email}
                  onChange={(e) => updateRow(references, setReferences, i, "email", e.target.value)} />
              </Field>
            </RepeatableRow>
          ))}
          <AddButton label="Add reference" onClick={() => setReferences([...references, { ...emptyReference }])} />
        </Section>

        <Section title="A Few Quick Questions">
          <div className="space-y-4">
            {[
              ["work_authorized", "Are you authorized to work in the United States?"],
              ["age_18_plus", "Are you 18 years of age or older?"],
              ["has_evv_smartphone", "Do you have a smartphone capable of running our EVV (visit verification) app?"],
              ["willing_background_check", "Are you willing to undergo a background check?"],
            ].map(([key, question]) => (
              <div key={key} className="flex items-center justify-between border-b border-gray-100 pb-3">
                <span className="text-sm text-gray-700 pr-4">{question}</span>
                <div className="flex gap-4 shrink-0">
                  <label className="flex items-center gap-1.5 text-sm">
                    <input type="radio" name={key} checked={form[key] === "true"}
                      onChange={() => setField(key, "true")} /> Yes
                  </label>
                  <label className="flex items-center gap-1.5 text-sm">
                    <input type="radio" name={key} checked={form[key] === "false"}
                      onChange={() => setField(key, "false")} /> No
                  </label>
                </div>
              </div>
            ))}
            <Field label="How far are you willing to travel for a shift? (miles)">
              <input type="number" className={inputClass + " max-w-xs"} style={inputStyle}
                value={form.travel_radius_miles}
                onChange={(e) => setField("travel_radius_miles", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Anything else you'd like us to know?">
          <textarea
            className={inputClass}
            style={inputStyle}
            rows={4}
            value={form.applicant_notes}
            onChange={(e) => setField("applicant_notes", e.target.value)}
            placeholder="Optional — tell us about your experience, availability, or anything else relevant to your application."
          />
        </Section>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full md:w-auto px-8 py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: "#F3850C" }}
        >
          {submitting && <Spinner size={18} />}
          {submitting ? "Submitting…" : "Submit Application"}
        </button>
      </form>
    </div>
  );
}
